import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Paginator } from '@bindoc-projects/platform-lib/data';
import {
  BdAdminPermissionService,
  BdCompanyContextService,
  BdGraphApiUser,
  BdShareablesService,
  BdPermissionsUsersAdminService,
} from '@bindoc-projects/bindoc-permissions-lib/data';
import {
  AutoComplete,
  AutoCompleteCompleteEvent,
  AutoCompleteSelectEvent,
} from 'primeng/autocomplete';
import { IftaLabel } from 'primeng/iftalabel';
import { take } from 'rxjs';

import {
  BdUserSearchItemComponent,
  userLabel,
} from './user-search-item.component';

@Component({
  selector: 'bd-user-search',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <p-iftalabel>
      <p-autocomplete
        [suggestions]="items()"
        [forceSelection]="true"
        [virtualScroll]="true"
        [virtualScrollItemSize]="56"
        fluid
        variant="filled"
        [optionLabel]="userLabel"
        appendTo="body"
        placeholder="Nutzer suchen..."
        (completeMethod)="onComplete($event)"
        (onSelect)="onSelect($event)"
      >
        <ng-template let-user #item>
          <bd-user-search-item
            [user]="user"
            [isAdmin]="isAdmin()"
          ></bd-user-search-item>
        </ng-template>
      </p-autocomplete>
      <label>Nutzer</label>
    </p-iftalabel>
  `,
  imports: [FormsModule, AutoComplete, IftaLabel, BdUserSearchItemComponent],
})
export class BdUserSearchComponent implements OnInit {
  public readonly userSelect = output<BdGraphApiUser>();

  public readonly items = signal<BdGraphApiUser[]>([]);
  public readonly isAdmin = signal(false);
  protected readonly userLabel = userLabel;

  private readonly _adminUserService = inject(BdPermissionsUsersAdminService);
  private readonly _resourcesService = inject(BdShareablesService);
  private readonly _companyContext = inject(BdCompanyContextService);
  private readonly _adminPermissions = inject(BdAdminPermissionService);

  ngOnInit(): void {
    this._adminPermissions
      .containsPermission(['ADMIN:USER'])
      .pipe(take(1))
      .subscribe((hasAdmin) => this.isAdmin.set(hasAdmin));
  }

  public onComplete(event: AutoCompleteCompleteEvent): void {
    const query = event.query;
    if (this.isAdmin()) {
      this._adminUserService
        .search(Paginator.of({ pageSize: 20, query }))
        .subscribe((page) => this.items.set(page.content));
    } else {
      this._resourcesService
        .myUsers({ companyId: this._companyContext.currentCompanyId, query })
        .subscribe((page) =>
          // Non-admin users must not be able to select disabled accounts.
          this.items.set(page.content.filter((user) => user.isAccountEnabled)),
        );
    }
  }

  public onSelect(event: AutoCompleteSelectEvent): void {
    this.userSelect.emit(event.value as BdGraphApiUser);
  }
}
